import { colors, Icon, Stack, Tooltip, Typography } from '@ui-components/neo.tax';
import { ReactComponent as OpenLink } from '@ui-components/neo.tax/dist/icons/open-link.svg';
import React, { ReactElement } from 'react';

import { LinkedPullRequest, TicketLinkSignal } from '@neo.tax/shared/graphql';
import { formatDate } from '@neo.tax/shared/utils';

import {
  LINKED_PULL_REQUEST_ROW_HEIGHT,
  LINKED_PULL_REQUESTS_VERTICAL_PADDING,
  LinkedPullRequestIdentifier,
  LinkedPullRequestMeta,
  LinkedPullRequestRowContainer,
  LinkedPullRequestsContainer,
  LinkedPullRequestTitle,
} from './tickets-list.styled';

export const getLinkedPullRequestsHeight = (count: number): number =>
  count === 0 ? 0 : count * LINKED_PULL_REQUEST_ROW_HEIGHT + 2 * LINKED_PULL_REQUESTS_VERTICAL_PADDING;

export type LinkedPullRequestItem = Pick<
  LinkedPullRequest,
  | 'pullRequestTicketId'
  | 'identifier'
  | 'title'
  | 'url'
  | 'authorName'
  | 'authorLogin'
  | 'completedDatetime'
  | 'isMerged'
  | 'signals'
  | 'matchedKey'
>;

const SIGNAL_LABEL: Record<TicketLinkSignal, string> = {
  [TicketLinkSignal.Branch]: 'branch name',
  [TicketLinkSignal.Title]: 'PR title',
  [TicketLinkSignal.Body]: 'PR description',
  [TicketLinkSignal.CommitMessage]: 'commit message',
  [TicketLinkSignal.ProviderAttachment]: 'attached in your ticketing tool',
  [TicketLinkSignal.FileColumn]: 'uploaded file',
};

const formatCompletion = ({ completedDatetime, isMerged }: LinkedPullRequestItem): string | null => {
  if (!completedDatetime) {
    return null;
  }
  const date = formatDate(completedDatetime);
  if (isMerged === true) {
    return `Merged ${date}`;
  }
  if (isMerged === false) {
    return `Closed ${date}`;
  }
  return `Completed ${date}`;
};

const formatReason = ({ matchedKey, signals }: LinkedPullRequestItem): string => {
  const reason = `Linked by ${signals.map((signal) => SIGNAL_LABEL[signal]).join(', ')}`;
  // The key is withheld from viewers with PII redacted.
  return matchedKey ? `${reason} (${matchedKey})` : reason;
};

const LinkedPullRequestRow = ({ pullRequest }: { pullRequest: LinkedPullRequestItem }): ReactElement => {
  const author = pullRequest.authorName ?? pullRequest.authorLogin;
  const completion = formatCompletion(pullRequest);
  const reason = formatReason(pullRequest);
  const meta = [author, completion].filter(Boolean).join(' · ');

  return (
    <LinkedPullRequestRowContainer direction="column" justifyContent="center" gap={1}>
      <Stack direction="row" alignItems="center" gap={2}>
        {pullRequest.identifier && (
          <LinkedPullRequestIdentifier fontSize="xs" color={colors.gray[500]}>
            {pullRequest.identifier}
          </LinkedPullRequestIdentifier>
        )}
        <LinkedPullRequestTitle title={pullRequest.title} fontSize="xs" fontWeight="medium">
          {pullRequest.title}
        </LinkedPullRequestTitle>
        {pullRequest.url && (
          <a href={pullRequest.url} target="_blank" rel="noreferrer" aria-label={`Open ${pullRequest.title}`}>
            <Icon node={<OpenLink />} size={3} colorVariant="none" />
          </a>
        )}
      </Stack>
      <Tooltip text={reason}>
        <LinkedPullRequestMeta fontSize="xs" color={colors.gray[500]}>
          {meta ? `${meta} · ${reason}` : reason}
        </LinkedPullRequestMeta>
      </Tooltip>
    </LinkedPullRequestRowContainer>
  );
};

export const LinkedPullRequests = ({
  id,
  linkedPullRequests,
}: {
  id: string;
  linkedPullRequests: LinkedPullRequestItem[];
}): ReactElement => (
  <LinkedPullRequestsContainer id={id} direction="column" aria-label="Linked pull requests">
    {linkedPullRequests.map((pullRequest) => (
      <LinkedPullRequestRow key={pullRequest.pullRequestTicketId} pullRequest={pullRequest} />
    ))}
  </LinkedPullRequestsContainer>
);

export const LinkedPullRequestsToggle = ({
  controlsId,
  count,
  isExpanded,
  onToggle,
}: {
  controlsId: string;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
}): ReactElement => (
  <Typography
    as="button"
    type="button"
    fontSize="xs"
    fontWeight="medium"
    color={colors.primary[600]}
    aria-expanded={isExpanded}
    aria-controls={controlsId}
    onClick={(event: React.MouseEvent): void => {
      event.stopPropagation();
      onToggle();
    }}
    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}
  >
    {count === 1 ? '1 linked PR' : `${count} linked PRs`} {isExpanded ? '▾' : '▸'}
  </Typography>
);
